import { useSessionStore } from '@/core/stores/sessionStore';
// core/services/communitiesService.ts — B4b: API-backed communities.
//
// Thin fetch wrappers over DRF /api/communities/. Reads hydrate the
// communityStore; mutations patch it. Derived reads (listJoined/Trending/
// Recommended) stay store-reading. Signatures + _resetForTest unchanged.

import type { Community, CommunityInvite } from '@/core/models/community';
import type { Post } from '@/core/models/post';
import { useCommunityStore } from '@/core/stores/communityStore';
import { usePostStore } from '@/core/stores/postStore';
import { seedCommunities } from '@/core/data/communities';
import { api } from '@/core/lib/apiClient';

function getCommunities(): Community[] {
  return useCommunityStore.getState().communities;
}

export interface CreateCommunityInput {
  name: string;
  category: string;
  tagline: string;
  description: string;
  coverTone: string;
  coverImageKey?: string;
}

export const communitiesService = {
  async list(): Promise<Community[]> {
    const owner = useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.userId;
    const communities = await api<Community[]>('/communities/');
    if (owner !== useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.userId)
      throw new Error('Account changed during loading');
    useCommunityStore.getState().setCommunities(communities);
    return communities;
  },

  async listJoined(): Promise<Community[]> {
    return getCommunities().filter((c) => c.joined);
  },

  async listTrending(): Promise<Community[]> {
    return getCommunities().filter((c) => c.trending);
  },

  async listRecommended(): Promise<Community[]> {
    return getCommunities().filter((c) => c.recommended);
  },

  async get(id: string): Promise<Community | null> {
    try {
      const c = await api<Community>(`/communities/${id}/`);
      const list = getCommunities();
      useCommunityStore
        .getState()
        .setCommunities(
          list.some((x) => x.id === id) ? list.map((x) => (x.id === id ? c : x)) : [c, ...list],
        );
      return c;
    } catch {
      return null;
    }
  },

  async toggleJoin(id: string): Promise<void> {
    const community = await api<Community>(`/communities/${id}/join/`, { method: 'POST' });
    useCommunityStore
      .getState()
      .setCommunities(getCommunities().map((c) => (c.id === id ? community : c)));
  },

  async invites(): Promise<CommunityInvite[]> {
    const invites = await api<CommunityInvite[]>('/communities/invites/');
    useCommunityStore.getState().setInvites(invites);
    return invites;
  },

  async createCommunityPost(communityId: string, body: string): Promise<string> {
    const post = await api<Post>(`/communities/${communityId}/posts/`, {
      method: 'POST',
      body: { body },
    });
    usePostStore.getState().prependPost(post);
    useCommunityStore.getState().addPostToCommunity(communityId, post.id);
    return post.id;
  },

  async create(input: CreateCommunityInput): Promise<Community> {
    const community = await api<Community>('/communities/', {
      method: 'POST',
      body: input,
    });
    useCommunityStore.getState().addCommunity(community);
    return community;
  },

  _resetForTest(): void {
    useCommunityStore.getState().setCommunities(
      seedCommunities.map((c) => ({
        ...c,
        rules: [...c.rules],
        moderators: c.moderators.map((m) => ({ ...m })),
        events: c.events.map((e) => ({ ...e })),
        postIds: [...c.postIds],
      })),
    );
  },
} as const;
